import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ImageGalleryProps {
    images: { url: string; alt?: string }[];
    title: string;
}

const ImageGallery = ({ images, title }: ImageGalleryProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    if (images.length === 0) return null;

    const nextImage = () => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToImage = (index: number) => {
        setCurrentIndex(index);
    };

    // Generate alt text: use provided alt or fallback to title + index
    const getAltText = (index: number) => {
        return images[index].alt || `${title} - Bild ${index + 1}`;
    };

    return (
        <>
            {/* Main Preview Image */}
            <div
                className="aspect-video overflow-hidden cursor-pointer group relative"
                onClick={() => setIsOpen(true)}
            >
                <img
                    src={images[currentIndex].url}
                    alt={getAltText(currentIndex)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                />
                {images.length > 1 && (
                    <>
                        {/* Image counter */}
                        <div className="absolute top-2 right-2 bg-bg/80 text-text px-2 py-1 rounded text-sm">
                            {currentIndex + 1} / {images.length}
                        </div>
                        {/* Expand hint */}
                        <div className="absolute inset-0 bg-bg/0 group-hover:bg-bg/20 transition-colors flex items-center justify-center">
                            <span className="text-text opacity-0 group-hover:opacity-100 transition-opacity bg-bg/80 px-4 py-2 rounded">
                                Galerie anzeigen
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Thumbnail Navigation (if more than 1 image) */}
            {images.length > 1 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                    {images.map((image, idx) => (
                        <button
                            key={idx}
                            onClick={() => goToImage(idx)}
                            className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                                idx === currentIndex
                                    ? "border-primary scale-105"
                                    : "border-text/20 hover:border-text/40"
                            }`}
                        >
                            <img
                                src={image.url}
                                alt={getAltText(idx)}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </button>
                    ))}
                </div>
            )}

            {/* Full Screen Gallery Modal */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 z-[9999] bg-bg/95 flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                    >
                        <button
                            className="absolute top-4 right-4 text-text hover:text-primary text-3xl z-10"
                            onClick={() => setIsOpen(false)}
                        >
                            ×
                        </button>

                        <div
                            className="relative w-full max-w-5xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Main Gallery Image */}
                            <motion.img
                                key={currentIndex}
                                src={images[currentIndex].url}
                                alt={getAltText(currentIndex)}
                                className="w-full h-auto max-h-[80vh] object-contain rounded"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3 }}
                            />

                            {/* Navigation Arrows */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-bg/80 hover:bg-bg text-text p-3 rounded-full transition-colors"
                                        onClick={prevImage}
                                    >
                                        ←
                                    </button>
                                    <button
                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-bg/80 hover:bg-bg text-text p-3 rounded-full transition-colors"
                                        onClick={nextImage}
                                    >
                                        →
                                    </button>
                                </>
                            )}

                            {/* Image Counter */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-bg/80 text-text px-4 py-2 rounded">
                                {currentIndex + 1} von {images.length}
                            </div>

                            {/* Thumbnail Strip */}
                            <div className="mt-4 flex gap-2 justify-center overflow-x-auto pb-2">
                                {images.map((image, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => goToImage(idx)}
                                        className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition-all ${
                                            idx === currentIndex
                                                ? "border-primary scale-110"
                                                : "border-text/20 hover:border-text/40"
                                        }`}
                                    >
                                        <img
                                            src={image.url}
                                            alt={getAltText(idx)}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </button>
                                ))}
                            </div>

                            {/* Image Title */}
                            <p className="text-center text-text/80 mt-4">
                                {title} - Bild {currentIndex + 1}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ImageGallery;

